/**
 * Web Export Service Adapter
 *
 * Implements IExportService for the web platform using browser APIs.
 * Content generation is delegated to the shared exportPipeline; this module
 * handles only the browser-specific I/O (blob downloads, iframe print for PDF).
 *
 * PDF:      generateExportContent -> hidden iframe -> contentWindow.print()
 * MD/TXT:   generateExportContent -> Blob -> <a> download
 * DOCX:     generateExportContent -> Packer.toBlob() -> <a> download
 *
 * Bulk export (multi-chat) produces a ZIP via jszip (dynamically imported).
 * Chats are processed sequentially with AbortController-based cancellation.
 */
import { Packer } from 'docx';
import { Document as DocxDocument } from 'docx';
import {
    IExportService,
    ExportProgressCallback,
    ExportRequest,
    ExportResult,
    BulkExportRequest,
    BulkExportResult,
    PreviewRequest,
    PreviewResult,
    ExportFormat,
    generateExportContent,
    buildExportFilename,
    apiClient,
} from '@edgerunner/shared';
import iconUrl from '@edgerunner/shared/assets/icons/16x16.png?url';
import logoIconUrl from '@edgerunner/shared/assets/icons/128x128.png?url';
import katexCssText from 'katex/dist/katex.min.css?inline';

// ─── Assistant icon ───────────────────────────────────────────────────────────

let cachedAssistantIcon: string | null = null;

async function getAssistantIconDataUrl(): Promise<string | null> {
    if (cachedAssistantIcon !== null) return cachedAssistantIcon || null;
    try {
        const res = await fetch(iconUrl);
        const blob = await res.blob();
        const dataUrl = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
        cachedAssistantIcon = dataUrl;
        return dataUrl;
    } catch {
        cachedAssistantIcon = '';
        return null;
    }
}

// ─── DOCX branding logo ───────────────────────────────────────────────────────

let cachedDocxLogo: Uint8Array | null = null;
let docxLogoFetched = false;

async function getDocxLogoPng(): Promise<Uint8Array | null> {
    if (docxLogoFetched) return cachedDocxLogo;
    try {
        const res = await fetch(logoIconUrl);
        const buffer = await res.arrayBuffer();
        cachedDocxLogo = new Uint8Array(buffer);
    } catch {
        cachedDocxLogo = null;
    }
    docxLogoFetched = true;
    return cachedDocxLogo;
}

// ─── Bulk export cancellation ────────────────────────────────────────────────

let bulkExportController: AbortController | null = null;

/** File extension used when adding content to a ZIP archive. */
const ZIP_EXTENSION_MAP: Partial<Record<ExportFormat, string>> = {
    pdf: 'html', // PDF format stored as HTML in ZIP (no browser print available)
    markdown: 'md',
    txt: 'txt',
    docx: 'docx',
};

// ─── Download helpers ─────────────────────────────────────────────────────────

function triggerBlobDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 5000);
}

function triggerTextDownload(content: string, filename: string, mimeType: string): void {
    triggerBlobDownload(new Blob([content], { type: mimeType }), filename);
}

function printViaIframe(html: string, title: string): Promise<void> {
    return new Promise((resolve, reject) => {
        const iframe = document.createElement('iframe');
        // Must be visible and sized for the browser to fully lay out the document.
        // Fixed width matches A4 page at 96 DPI (794px = 8.27in * 96). Using 100vw on a wide
        // monitor causes the browser to lay out content at viewport width then shrink-to-fit
        // scale it to the physical page — making everything tiny in the printed output.
        iframe.style.cssText = 'position:fixed;top:0;left:0;width:794px;height:100vh;border:none;z-index:-1;opacity:0.01;pointer-events:none;';
        document.body.appendChild(iframe);

        const cleanup = () => {
            setTimeout(() => {
                try { document.body.removeChild(iframe); } catch (e) { reject(e); }
                resolve();
            }, 1000);
        };

        try {
            const doc = iframe.contentDocument!;
            doc.open();
            doc.write(html);
            doc.close();
            doc.title = title;

            // Wait for fonts/images to load before printing.
            setTimeout(() => {
                try {
                    iframe.contentWindow!.focus();
                    iframe.contentWindow!.print();
                    cleanup();
                } catch (e) {
                    reject(e);
                }
            }, 500);
        } catch (e) {
            document.body.removeChild(iframe);
            reject(e);
        }
    });
}

// ─── IExportService implementation ───────────────────────────────────────────

export const webExportService: IExportService = {
    getPlatform: () => 'web',

    exportChat: async (request: ExportRequest): Promise<ExportResult> => {
        try {
            const { messages, options, chatId } = request;
            const { format } = options;
            const { base, full: filename } = buildExportFilename(chatId, format);

            const content = await generateExportContent(messages, format, options, katexCssText, getAssistantIconDataUrl, getDocxLogoPng);

            if (format === 'pdf') {
                await printViaIframe(content as string, base);
                return { success: true, successMessage: 'Print dialog opened' };
            }

            if (format === 'docx') {
                const blob = await Packer.toBlob(content as DocxDocument);
                triggerBlobDownload(blob, filename);
                return { success: true, successMessage: 'Download started' };
            }

            const mime =
                format === 'markdown' ? 'text/markdown;charset=utf-8' : 'text/plain;charset=utf-8';
            triggerTextDownload(content as string, filename, mime);
            return { success: true, successMessage: 'Download started' };
        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown export error',
            };
        }
    },

    exportBulk: async (
        request: BulkExportRequest,
        onProgress?: ExportProgressCallback,
    ): Promise<BulkExportResult> => {
        const { chatIds, options } = request;
        const { format } = options;
        const totalCount = chatIds.length;

        // Empty selection: early return
        if (totalCount === 0) {
            return { success: true, exportedCount: 0, totalCount: 0 };
        }

        // Single chat: delegate to exportChat for a direct file download
        if (totalCount === 1) {
            try {
                const messages = await apiClient.history.getHistoryById(chatIds[0]);
                if (!Array.isArray(messages) || messages.length === 0) {
                    return {
                        success: false,
                        exportedCount: 0,
                        totalCount: 1,
                        errors: [`Chat ${chatIds[0]}: No messages found`],
                    };
                }
                const singleOptions = { ...options, footerText: `Chat ID: ${chatIds[0]}` };

                const result = await webExportService.exportChat({
                    chatId: chatIds[0],
                    messages,
                    options: singleOptions,
                });
                onProgress?.(1, 1, chatIds[0]);
                return {
                    success: result.success,
                    exportedCount: result.success ? 1 : 0,
                    totalCount: 1,
                    errors: result.error ? [result.error] : undefined,
                };
            } catch (error) {
                const msg = error instanceof Error ? error.message : 'Unknown error';
                onProgress?.(1, 1, chatIds[0]);
                return {
                    success: false,
                    exportedCount: 0,
                    totalCount: 1,
                    errors: [`Chat ${chatIds[0]}: ${msg}`],
                };
            }
        }

        // Multi-chat: ZIP export
        // Abort any in-flight export before starting a new one (prevents double-click race)
        if (bulkExportController !== null) {
            bulkExportController.abort();
        }
        const thisController = new AbortController();
        bulkExportController = thisController;
        const { signal } = thisController;
        const errors: string[] = [];
        let exportedCount = 0;

        try {
            const JSZip = (await import('jszip')).default;
            const zip = new JSZip();

            // Fetch chat list once to resolve titles per-chat
            const chatList = await apiClient.history.listHistory();
            const titleMap = new Map(chatList.map((c) => [c.id, c.title]));

            for (let i = 0; i < chatIds.length; i++) {
                // Check for cancellation before each chat
                if (signal.aborted) {
                    errors.push('Export cancelled by user');
                    break;
                }

                const chatId = chatIds[i];
                onProgress?.(i, totalCount, chatId);

                try {
                    const messages = await apiClient.history.getHistoryById(chatId);

                    if (!Array.isArray(messages) || messages.length === 0) {
                        errors.push(`Chat ${chatId}: No messages found`);
                        continue;
                    }

                    // Per-chat options: footer with chatId, title from chat list
                    const chatTitle = titleMap.get(chatId);
                    const clampedTitle = chatTitle && chatTitle.length > 50
                        ? chatTitle.substring(0, 47) + '...'
                        : chatTitle;
                    const perChatOptions = {
                        ...options,
                        footerText: `Chat ID: ${chatId}`,
                        title: clampedTitle,
                    };

                    const content = await generateExportContent(
                        messages,
                        format,
                        perChatOptions,
                        katexCssText,
                        getAssistantIconDataUrl,
                        getDocxLogoPng,
                    );

                    const { base } = buildExportFilename(chatId, format);
                    const ext = ZIP_EXTENSION_MAP[format];
                    const filename = `${base}.${ext}`;

                    if (format === 'docx') {
                        const blob = await Packer.toBlob(content as DocxDocument);
                        zip.file(filename, blob);
                    } else {
                        zip.file(filename, content as string);
                    }

                    exportedCount++;
                } catch (error) {
                    const msg = error instanceof Error ? error.message : 'Unknown error';
                    errors.push(`Chat ${chatId}: ${msg}`);
                }
            }

            // Final progress update (mirrors Electron: fires regardless of completion/cancellation)
            onProgress?.(totalCount, totalCount, '');

            // If cancelled after the loop, skip ZIP generation entirely
            if (signal.aborted) {
                if (!errors.includes('Export cancelled by user')) {
                    errors.push('Export cancelled by user');
                }
                return {
                    success: false,
                    exportedCount,
                    totalCount,
                    errors,
                };
            }

            // If nothing succeeded, skip ZIP download
            if (exportedCount === 0) {
                return {
                    success: false,
                    exportedCount: 0,
                    totalCount,
                    errors: errors.length > 0 ? errors : ['No chats were exported successfully'],
                };
            }

            // Generate and download ZIP
            const zipBlob = await zip.generateAsync({ type: 'blob' });
            const date = new Date().toISOString().split('T')[0];
            triggerBlobDownload(zipBlob, `chat-export-${date}.zip`);

            return {
                success: exportedCount > 0,
                exportedCount,
                totalCount,
                errors: errors.length > 0 ? errors : undefined,
            };
        } catch (error) {
            const msg = error instanceof Error ? error.message : 'Unknown error';
            return {
                success: false,
                exportedCount,
                totalCount,
                errors: [...errors, `Bulk export failed: ${msg}`],
            };
        } finally {
            // Only clear the module-level ref if it's still our controller instance
            if (bulkExportController === thisController) {
                bulkExportController = null;
            }
        }
    },

    generatePreview: async (request: PreviewRequest): Promise<PreviewResult> => {
        try {
            const { messages, options } = request;
            // DOCX is binary — render as markdown for preview purposes.
            const previewFormat: ExportFormat =
                options.format === 'docx' ? 'markdown' : options.format;
            const content = await generateExportContent(messages, previewFormat, {
                ...options,
                format: previewFormat,
            }, katexCssText, getAssistantIconDataUrl, getDocxLogoPng);
            return { content: content as string };
        } catch (error) {
            return {
                content: `<p class="error">${error instanceof Error ? error.message : 'Preview generation failed'}</p>`,
            };
        }
    },

    cancelBulkExport: async (): Promise<void> => {
        bulkExportController?.abort();
    },

    // openFile and showInFolder are Electron-only shell operations.
    openFile: undefined,
    showInFolder: undefined,
};
